import { Text, WasabiButton } from '@kk/ui';
import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';

import { useClients, useContentCan, useSession } from '@/auth/SessionProvider';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';

/**
 * **THE DARK RAIL: "you are in Kiddie Kredit's own admin, not an org's portal"** — the board's own note, and
 * the only job this shell has that the org shell does not.
 *
 * A super admin frequently IS staff of a club too, and the two surfaces show different things about the same
 * children. Sharing one chrome would make "which hat am I wearing" a thing to remember, and this project has
 * lost that bet every time (Corollary A-3). So the difference is drawn rather than remembered.
 *
 * It carries one nav item, because one screen is built. The board draws a longer rail — Organizations,
 * Starter library, Copy deck, Support — and none of those exist; a rail of five links where four go nowhere
 * is the "coming soon" the org shell already refuses.
 */
/** Where the browser saves a file; replaceable in tests (jsdom has no downloads). */
export const adminShellEffects = {
  save(name: string, text: string) {
    const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  },
};

/**
 * **O10's "Export JSON" — the database is the source, and this is the repo's copy** (content engine part 4). What is
 * live, in `docs/handoff/quest-content`'s own format, saved as one file for `pnpm --filter api export:quest --from`.
 */
function ExportJson() {
  const { data } = useClients();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="admin-rail-button"
      disabled={busy}
      data-testid="export-json"
      onClick={() => {
        setBusy(true);
        data
          .exportContent()
          .then((out) =>
            adminShellEffects.save(
              `quest-content-${out.exportedAt.slice(0, 10)}.json`,
              `${JSON.stringify(out, null, 2)}\n`,
            ),
          )
          .catch(() => undefined)
          .finally(() => setBusy(false));
      }}
    >
      {deck.o11.exportJson}
    </button>
  );
}

export function AdminShell() {
  const { session } = useSession();
  const { auth } = useClients();
  const onIcons = useLocation().pathname.startsWith('/quest/icons');
  const owner = useContentCan('team');
  return (
    <div className="admin-frame">
      <aside className="admin-rail">
        <div className="admin-rail-head">
          <Text variant="chipSelected" tone="white">
            {deck.o06.railTitle}
          </Text>
          {/* @kk/ui has no muted-on-dark tone and this is the only screen that wants one, so it is CSS here
              rather than a token added to the design system for one rail */}
          <span className="admin-rail-sub">{deck.o06.railSubtitle}</span>
        </div>
        <nav className="admin-nav" aria-label={deck.o06.railSubtitle}>
          <NavLink
            to="/quest"
            // the missions live under /quest, and so does the icon library, which has its own link below
            className={({ isActive }) => `admin-nav-item${isActive && !onIcons ? ' active' : ''}`}
          >
            {deck.o06.nav}
          </NavLink>
          <NavLink
            to="/quest/icons"
            className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
            data-testid="nav-icons"
          >
            {deck.o14.nav}
          </NavLink>
          <NavLink
            to="/team"
            className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
            data-testid="nav-team"
          >
            {deck.o17.nav}
          </NavLink>
          <NavLink
            to="/help"
            className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
            data-testid="nav-help"
          >
            {deck.help.nav}
          </NavLink>
          <NavLink
            to="/village"
            end
            className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
            data-testid="nav-village"
          >
            {deck.vsq.nav}
          </NavLink>
          <NavLink
            to="/village/partners"
            className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
            data-testid="nav-village-partners"
          >
            {deck.vsq.partners.nav}
          </NavLink>
          {/* O18: owners only — the page refuses anyone else too */}
          {owner ? (
            <NavLink
              to="/applications"
              className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
              data-testid="nav-applications"
            >
              {deck.o18.nav}
            </NavLink>
          ) : null}
          {/* DECISIONS 92: club billing, owners only — the server refuses anyone else too */}
          {owner ? (
            <NavLink
              to="/billing"
              className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
              data-testid="nav-club-billing"
            >
              {deck.clubBilling.nav}
            </NavLink>
          ) : null}
          {/* the admin's metrics: owners only — the server refuses anyone else too */}
          {owner ? (
            <NavLink
              to="/metrics"
              className={({ isActive }) => `admin-nav-item${isActive ? ' active' : ''}`}
              data-testid="nav-metrics"
            >
              {deck.metrics.nav}
            </NavLink>
          ) : null}
        </nav>
        <ExportJson />
        {/*
         * **THE SAME PLACE IN EACH SHELL** (Evan, 2026-09-23): the club portal's sign-out sits at the bottom
         * of its sidebar under the signed-in identity, so this one does too. The admin rail had no sign-out
         * at all — which on the host whose own refusal offers "Sign Out" as a way back was the odd gap.
         */}
        <div className="admin-rail-foot">
          <span className="admin-rail-sub">{session?.user.email || session?.user.sub}</span>
          <WasabiButton
            label={placeholders.signIn.signOut}
            variant="text"
            textSize="sm"
            fullWidth={false}
            onPress={() => void auth.signOut()}
            testID="sign-out"
          />
        </div>
      </aside>
      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
